import { useMutation } from '@apollo/client';
import { Button } from 'components/Button/Button';
import { Loader } from 'components/Loader/Loader';
import { Modal } from 'components/Modal/Modal';
import { Portal } from 'components/Portal/Portal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EXPORT_MEMBERS } from 'services/users/mutation';
import { downloadCsv } from 'utils/downloadCsv';

export const ExportMembers = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [csvData, setCsvData] = useState<{ content: string; filename: string } | null>(null);
    const [searchParams] = useSearchParams();
    const { t } = useTranslation('translation', { keyPrefix: 'members' });
    const [exportMembers] = useMutation(EXPORT_MEMBERS);

    const closeModal = () => {
        setIsOpen(false);
        setCsvData(null);
    };

    const doExport = async () => {
        setIsOpen(true);
        setLoading(true);
        const search = searchParams.get('search') || undefined;
        const sort = searchParams.get('sort') || 'A_Z';
        const status = searchParams.get('status')?.split(',') || ['ACTIVE'];
        const property = searchParams.get('property');
        try {
            const { data } = await exportMembers({
                variables: {
                    search,
                    sort,
                    status,
                    isShareholder: property === 'shareholder' ? true : null,
                    hasNegativeBalance: property === 'negative_balance' ? true : null,
                    hasRemainderBalance: property === 'remainder_balance' ? true : null,
                },
            });
            if (data?.exportMembers?.content) {
                const content: string = data.exportMembers.content;
                const filename: string = data.exportMembers.filename;
                setCsvData({ content, filename });
                downloadCsv(content, filename);
            }
        } catch (_) {
            // handled by ErrorContent
        } finally {
            setLoading(false);
        }
    };

    const LoadingContent = () => {
        return (
            <>
                <div>
                    <div className="text-xl font-semibold text-zinc-700 mb-2">{t('exportingTitle')}</div>
                    <div className="text-sm text-zinc-500">{t('exportingText')}</div>
                </div>
                <div className="flex items-center justify-center">
                    <Loader />
                </div>
            </>
        );
    };

    const LoadedContent = () => {
        return (
            <>
                <div>
                    <div className="text-xl font-semibold text-zinc-700 mb-2">{t('exportReadyTitle')}</div>
                    <div className="text-sm text-zinc-500">{t('exportReadyText')}</div>
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={closeModal}>
                        {t('exportCancelButton')}
                    </Button>
                    {csvData && (
                        <Button onClick={() => downloadCsv(csvData.content, csvData.filename)}>
                            {t('exportDownloadButton')}
                        </Button>
                    )}
                </div>
            </>
        );
    };

    const ErrorContent = () => {
        return (
            <>
                <div>
                    <div className="text-xl font-semibold text-zinc-700 mb-2">{t('exportErrorTitle')}</div>
                    <div className="text-sm text-zinc-500">{t('exportErrorText')}</div>
                </div>
                <div className="flex justify-end gap-2">
                    <Button onClick={closeModal}>{t('exportCloseButton')}</Button>
                </div>
            </>
        );
    };

    return (
        <>
            <Button variant="outline" onClick={() => doExport()}>
                {t('exportCsvButton')}
            </Button>
            <Portal>
                <Modal isOpen={isOpen} onClose={closeModal} maxWidth={512}>
                    <div className="flex flex-col gap-4">
                        {loading && !csvData && <LoadingContent />}
                        {!loading && csvData && <LoadedContent />}
                        {!loading && !csvData && <ErrorContent />}
                    </div>
                </Modal>
            </Portal>
        </>
    );
};
